import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Gamepad2, ShieldCheck, DollarSign, ShoppingCart, User, Settings,
  Layers, Package, CheckCircle2, Clock, AlertTriangle, ChevronRight,
  Search, Upload, Eye, Download, ArrowRight, RefreshCw, Plus, Trash2,
  Menu, X, Sparkles, CreditCard, Lock, ArrowUpRight, HelpCircle, FileText,
  LogOut, Edit3, ScanLine, ShieldAlert, XCircle, Copy, KeyRound, Check,
  MessageCircle, Send, Paperclip, Wallet, ChevronDown,
  Zap, Flame,
  Trophy, Medal, FileDown, Crown, Gift
} from "lucide-react";
import * as api from "./api";
import { saveEntry as saveCredentialEntry } from "./lib/credentialHistory";

// Extracted presentational components (see /components/ tree).
import { Toast } from "./components/shared/Toast";
import { FormField } from "./components/shared/FormField";
import { Modal } from "./components/shared/Modal";
import { TabBtn } from "./components/shared/TabBtn";
import { Metric } from "./components/shared/Metric";
import { CredRow } from "./components/shared/CredRow";
import { StatusBadge, UserStatusBadge } from "./components/shared/StatusBadges";
import { PublicStats } from "./components/shared/PublicStats";
import { SocialProofFeed } from "./components/shared/SocialProofFeed";
import { WalletChip, HeaderWalletBadge } from "./components/shared/WalletChip";
import { OCRProgress } from "./components/shared/OCRProgress";
import { ChatButton } from "./components/chat/ChatButton";
import { OrdersPage } from "./pages/OrdersPage";
import TournamentsPage from "./pages/TournamentsPage";
import { useAdminActivity } from "./features/admin/useAdminActivity";
import { useLivePricing } from "./features/catalog/useLivePricing";
import { AdminOfflineNotice } from "./components/orders/AdminOfflineNotice";
import { AdminDashboardTab } from "./pages/admin/AdminDashboardTab";
import { AdminDivisaTab } from "./pages/admin/AdminDivisaTab";
import { AdminPagosTab } from "./pages/admin/AdminPagosTab";
import { ChatDrawer } from "./components/chat/ChatDrawer";
import { ForensicReport } from "./components/game/ForensicReport";
import { GameBannerCarousel } from "./components/game/GameBannerCarousel";
import { GameFaq } from "./components/game/GameFaq";
import { GameCard, GameCardSkeleton } from "./components/game/GameCard";
import { GameCatalogPage } from "./pages/GameCatalogPage";
import { TopUsersRanking } from "./components/admin/TopUsersRanking";
import { ResetTestDataCard } from "./components/admin/ResetTestDataCard";
import { CatalogPortability } from "./components/admin/CatalogPortability";
import { AdminPushToggle } from "./components/admin/AdminPushToggle";
import { ReopenNotifyButton } from "./components/client/ReopenNotifyButton";
import { SocialNetworksBar } from "./components/client/SocialNetworksBar";
import { WelcomeModal } from "./components/WelcomeModal";
import { playChime, playChimeSound, playChatChime } from "./lib/notificationSound";
import { useUI } from "./contexts/UIContext";
import { useConfig } from "./contexts/ConfigContext";
import { useAdminUsers } from "./features/admin/useAdminUsers";
import { useAdminSocial } from "./features/admin/useAdminSocial";
import { useAdminBanners } from "./features/admin/useAdminBanners";
import { useSiteStatus } from "./features/admin/useSiteStatus";
import { useAdminBanks } from "./features/admin/useAdminBanks";
import { useAdminCatalog } from "./features/admin/useAdminCatalog";
import { useOrderPolling } from "./features/orders/useOrderPolling";
import { usePartialPayment } from "./features/checkout/usePartialPayment";
import { useShoppingFlow } from "./features/checkout/useShoppingFlow";
import { usePaymentSubmit } from "./features/checkout/usePaymentSubmit";
import { useTour } from "./components/tour/TourProvider";
import { TourLauncherButton } from "./components/tour/TourLauncherButton";
import { AppProvider } from "./AppContext";
import { AdminPage } from "./pages/AdminPage";
import { AppModals } from "./pages/AppModals";
import { CurrencyUsdConfirmDialog } from "./components/shared/CurrencyUsdConfirmDialog";
import { StoreDetailPage } from "./pages/StoreDetailPage";
import { AuthPage } from "./pages/AuthPage";
import { ReferralBanner } from "./components/client/ReferralBanner";
import { ReferralPage } from "./pages/ReferralPage";
import { ResellerWithdrawals } from "./components/client/ResellerWithdrawals";
import { captureReferralFromUrl, pendingReferralCode, clearPendingReferralCode } from "./lib/referralAttribution";
import { useAuth } from "./hooks/useAuth";
import { useLocation, useNavigate, useNavigationType, matchPath } from "react-router-dom";

const LOGO_URL = "https://customer-assets-rejwkqb3.emergentagent.net/job_ag7-game-recharge/artifacts/jx68vmm8_logo.webp";
// Default fallback if backend settings are unreachable. Admin can change the
// active value from Panel Admin → Divisa & Soporte.
const DEFAULT_SUPPORT_WHATSAPP = "584140000000";

// F3B-7 — ids válidos de tabs Admin para la ruta /admin/:tab (mismos ids de AdminPage.jsx).
const ADMIN_TAB_IDS = ["dashboard", "paquetes", "pedidos", "pagos", "usuarios", "top", "ganancias", "reportes", "divisa", "redes", "banners", "seguridad"];

export default function App() {
  // Interactive onboarding tour (context provided at /src/index.js).
  const tour = useTour();

  // ============ AUTH STATE ============
  const [user, setUser] = useState(null); // null = not logged in
  const [authLoading, setAuthLoading] = useState(true);
  // Auth state + handlers extracted to useAuth() below (after showToast/loadOrders exist).

  // Re-fetches /auth/me and updates `user` — used after actions that move
  // wallet balances outside the orders flow (e.g. reseller withdrawals).
  const refreshUser = useCallback(async () => {
    try {
      const u = await api.me();
      setUser(u);
      return u;
    } catch (e) {
      console.warn("refreshUser failed:", e);
      return null;
    }
  }, []);

  // ============ APP DATA ============
  const [activeRole, setActiveRole] = useState("client");
  const [clientTab, setClientTab] = useState("store");
  const [adminTab, setAdminTab] = useState("dashboard");

  // On mount + when a push notification is clicked, honour ?adminTab=X so the
  // admin lands on the right tab (e.g. "pedidos" after a "new order" push).
  useEffect(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      const t = p.get("adminTab");
      const map = { orders: "pedidos", pedidos: "pedidos", dashboard: "dashboard" };
      if (t && map[t]) setAdminTab(map[t]);
    } catch (e) { console.warn("adminTab query parse failed:", e); }
    // Listen for messages coming from the service worker (notificationclick).
    const onMsg = (ev) => {
      const d = ev?.data;
      if (d?.type === "ag7-push-click") {
        setAdminTab("pedidos");
      } else if (d?.type === "ag7-push-received") {
        // Play in-app chime so the admin also hears something when the tab is
        // open (some OSes mute background notifications when the tab is focused).
        try { playChime(); } catch (e) { console.warn("playChime failed:", e); }
      }
    };
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.addEventListener("message", onMsg);
      return () => navigator.serviceWorker.removeEventListener("message", onMsg);
    }
    return undefined;
  }, []);
  // Filter for the admin Orders view. "all" | "needs_review" | "pending" | "processing" | "rejected"
  const [orderFilter, setOrderFilter] = useState("all");
  // PASO 3 (F3B): toast/clipboard y moneda/formatters viven en providers.
  const { toast, showToast, copyToClipboard } = useUI();
  const { exchangeRate, setExchangeRate, invalidateExchangeRate, currency, setCurrency, rateReady,
    usdToBs, formatBs, formatUsd, formatMoney, formatItemMoney } = useConfig();

  const [supportWhatsapp, setSupportWhatsapp] = useState(DEFAULT_SUPPORT_WHATSAPP);
  const [games, setGames] = useState(() => {
    // Instant-load the catalog from the last successful fetch so the Home
    // grid renders immediately (no white flash) while the API refreshes in
    // the background. If localStorage is empty or corrupted, start empty
    // and rely on the skeleton grid.
    try {
      const raw = window.localStorage?.getItem("ag7_games_cache");
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) { console.warn("games cache read failed:", e); return []; }
  });
  const [gamesLoaded, setGamesLoaded] = useState(false); // true once /games responded at least once
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [activeTournamentCount, setActiveTournamentCount] = useState(0);
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    if (!user?.id || activeRole !== "client") { setActiveTournamentCount(0); return; }
    let alive = true;
    api.listTournaments()
      .then((out) => {
        if (alive) setActiveTournamentCount((out.tournaments || []).filter((tournament) => tournament.status === "published").length);
      })
      .catch(() => { if (alive) setActiveTournamentCount(0); });
    return () => { alive = false; };
  }, [user?.id, activeRole]);
  const [referralData, setReferralData] = useState(null);
  const [referralLoading, setReferralLoading] = useState(false);
  const [referralRewardPulse, setReferralRewardPulse] = useState(false);
  const previousReferralRewardsRef = React.useRef(null);

  // PASO 4 (F3B): estado y handlers administrativos extraídos a features/admin.
  const { users, loadingUsers, loadUsers, userRoleFilter, setUserRoleFilter,
    userSearch, setUserSearch, deleteUserConfirm, setDeleteUserConfirm,
    statusChangeModal, setStatusChangeModal, statusChangeReason, setStatusChangeReason,
    clientHistory, setClientHistory, clientHistoryLoading,
    handleChangeUserStatus, handleToggleUserRole, requestStatusChange,
    handleDeleteUser, openClientHistory } = useAdminUsers(showToast, user);
  const { socialNetworks, adminSocialNets, snForm, setSnForm, snBusy,
    loadSocialNetworksPublic, loadAdminSocialNets,
    handleSaveSocial, handleToggleSocial, handleDeleteSocial } = useAdminSocial(showToast);
  const { banners, bannerForm, setBannerForm, bannerBusy, bannerUploadingIdx,
    activeGameBanner, setActiveGameBanner, loadBanners, loadActiveGameBanner,
    resetBannerForm, handleBannerImageUpload, handleRemoveBannerImage,
    handleSaveBanner, handleToggleBannerActive, handleDeleteBanner } = useAdminBanners(showToast);
  const { siteStatus, siteStatusForm, setSiteStatusForm, siteStatusBusy,
    loadSiteStatus, handleToggleSiteOpen, handleSaveClosureReason } = useSiteStatus(showToast);


  // ============ SHOPPING FLOW ============
  const [selectedGame, setSelectedGame] = useState(null);

  // Shopping cart
  const [cart, setCart] = useState([]);
  const cartTotalBs = cart.reduce((a, it) => a + it.priceBs, 0);
  const cartTotalUsd = cart.reduce((a, it) => a + (Number(it.priceUsd) || 0), 0);

  // Payment form
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  // La moneda arranca en Bs: cambiar a $ exige confirmación explícita.
  const [confirmUsdSwitch, setConfirmUsdSwitch] = useState(false);

  // Public stats (for login screen counter)
  const [publicStats, setPublicStats] = useState({ completed_today: 0, completed_total: 0, active_games: 0 });

  // Admin modals
  // P4B-1 (F3): CRUD de paquetes + verificationGuide extraído a features/admin/useAdminCatalog.
  const {
    showAddPkgModal, setShowAddPkgModal,
    pkgGameId, setPkgGameId,
    pkgNameInput, setPkgNameInput,
    pkgPriceUsdInput, setPkgPriceUsdInput,
    pkgDescriptionInput, setPkgDescriptionInput,
    pkgImageInput, setPkgImageInput,
    pkgResellerPriceUsdInput, setPkgResellerPriceUsdInput,
    pkgCostPriceUsdInput, setPkgCostPriceUsdInput,
    pkgRequiresVerify, setPkgRequiresVerify,
    pkgVerifyTitle, setPkgVerifyTitle,
    pkgVerifyDescription, setPkgVerifyDescription,
    pkgVerifyImageUrl, setPkgVerifyImageUrl,
    pkgVerifyStepsText, setPkgVerifyStepsText,
    pkgIsBestSellerInput, setPkgIsBestSellerInput,
    editingPkg, setEditingPkg,
    resetPkgForm, handleSavePackage, handleDeletePackage,
    handlePkgImageFile, handlePkgVerifyImageFile,
    expandedGames, toggleExpandedGame,
    showGameModal, setShowGameModal,
    editingGame, setEditingGame,
    gameForm, setGameForm,
    savingGame, deleteGameConfirm, setDeleteGameConfirm,
    openCreateGameModal, openEditGameModal,
    handleGameImageFile, handleSaveGame, handleConfirmDeleteGame,
    handleUpdateGameCredType, handleToggleGameActive,
  } = useAdminCatalog({ games, setGames, showToast });

  // P4A (F3): estado y handlers de bancos extraídos a features/admin/useAdminBanks.
  const {
    showAddBankModal, setShowAddBankModal,
    editingPm, setEditingPm,
    EMPTY_BANK_FORM, bankForm, setBankForm,
    handleAddBank, handleDeleteBank, handleTogglePmActive, openEditPm,
  } = useAdminBanks({ paymentMethods, setPaymentMethods, showToast });

  // Admin: reject order modal
  const [rejectOrderModal, setRejectOrderModal] = useState(null); // order
  const [rejectReason, setRejectReason] = useState("");
  const [rejectingOrder, setRejectingOrder] = useState(false);
  // Default: reclaim any auto-credited surplus on reject (matches backend
  // default). Admin can uncheck the toggle in the reject modal.
  const [reclaimSurplusOnReject, setReclaimSurplusOnReject] = useState(true);
  // ─── Order adjustment workflow ────────────────────────────────────────
  // Admin picks a target order to swap for a different package + charge a
  // difference; client, meanwhile, pays that difference via a receipt.
  const [adjustOrderModal, setAdjustOrderModal] = useState(null); // admin: order being adjusted
  const [adjustPayOrder, setAdjustPayOrder] = useState(null); // client: order to pay difference for
  // Admin: manual override modal for a NEEDS_REVIEW order (side-by-side
  // receipt image + editable Monto/Referencia + APPROVE/REJECT decision).
  const [manualVerifyOrder, setManualVerifyOrder] = useState(null);
  // Track whether we've already shown the "N órdenes por verificar" toast
  // in this session, so the admin doesn't get spammed on every re-render.
  const needsReviewToastShownRef = React.useRef(false);

  // Support (WhatsApp) modal
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [supportDesc, setSupportDesc] = useState("");
  const [supportFile, setSupportFile] = useState(null);
  const [supportOrderId, setSupportOrderId] = useState("");
  const [supportSubmitting, setSupportSubmitting] = useState(false);


  // Reseller-aware price helpers: everything the client sees must go through effectiveUsd()
  // so the reseller wholesale price is shown consistently. Backend enforces the same rule
  // during checkout so the frontend cannot manipulate the final total.
  const isReseller = user?.role === "reseller";
  const effectiveUsd = (pkg) => {
    if (!pkg) return 0;
    if (isReseller && pkg.resellerPriceUsd && Number(pkg.resellerPriceUsd) > 0) {
      return Number(pkg.resellerPriceUsd);
    }
    return Number(pkg.priceUsd || 0);
  };




  // ============ INITIAL LOAD ============
  const loadPublicData = useCallback(async () => {
    // Use allSettled so a single flaky endpoint (stats, settings, ...) doesn't
    // block the catalog from rendering. Each block below only runs when its
    // own request succeeded.
    const [gmR, pmR, stR, psR] = await Promise.allSettled([
      api.listGames({ light: true }), api.listPaymentMethods(), api.getSettings(), api.getPublicStats()
    ]);
    if (gmR.status === "fulfilled") {
      const gm = gmR.value;
      setGames(gm);
      // Persist ONLY the lightweight metadata — game images are stored inline
      // as base64 data-URLs (~200-700 KB each) which blows through the 5 MB
      // localStorage quota when stringified. On next load we render from cache
      // (grey placeholder icons) and swap in the real images once the network
      // fetch resolves — the user still gets an instant layout.
      try {
        // Persist the lightweight metadata + image URLs (tiny strings since the
        // catalog migrated to object storage) so a reload paints cards AND
        // kicks off image downloads instantly, before /games responds. Any
        // legacy base64 blob is still blanked to keep the cache small.
        const light = gm?.map((g) => ({ ...g, image: (g.image || "").startsWith("data:") ? "" : g.image }));
        window.localStorage?.setItem("ag7_games_cache", JSON.stringify(light));
      } catch (e) { console.warn("games cache write failed (quota/private mode):", e); }
      setGamesLoaded(true);
      if (gm[0]) setPkgGameId(gm[0].id);
    } else {
      console.error("Games load failed", gmR.reason);
    }
    if (pmR.status === "fulfilled") {
      const pm = pmR.value;
      setPaymentMethods(pm);
      if (pm[0]) setSelectedPmId(pm[0].id);
    } else {
      console.error("Payment methods load failed", pmR.reason);
    }
    if (stR.status === "fulfilled") {
      const st = stR.value;
      const srvRate = Number(st?.exchange_rate);
      if (Number.isFinite(srvRate) && srvRate > 0) {
        setExchangeRate(srvRate);
        try { window.localStorage?.setItem("ag7_exchange_rate", String(srvRate)); }
        catch (e) { console.warn("exchange_rate cache write failed:", e); }
      }
      if (st.support_whatsapp) setSupportWhatsapp(st.support_whatsapp);
    } else {
      console.error("Settings load failed", stR.reason);
      // No mantenemos una cotización guardada cuando el servidor no puede
      // confirmarla. El catálogo queda sin Bs y las acciones de compra siguen
      // bloqueadas hasta el siguiente refresco autorizado.
      invalidateExchangeRate();
    }
    if (psR.status === "fulfilled") {
      setPublicStats(psR.value);
    } else {
      console.error("Public stats load failed", psR.reason);
    }
  }, [invalidateExchangeRate, setExchangeRate]);

  // Precios en tiempo real: si cambia la tasa o cualquier precio/paquete en el
  // backend, se recargan tasa + catálogo sin refrescar la página (visitantes y
  // clientes, pestañas abiertas durante horas/días).
  const refreshPricing = useCallback(async (payload) => {
    const liveRate = Number(payload?.exchange_rate);
    if (Number.isFinite(liveRate) && liveRate > 0) {
      setExchangeRate(liveRate);
      try { window.localStorage?.setItem("ag7_exchange_rate", String(liveRate)); }
      catch (e) { console.warn("exchange_rate cache write failed:", e); }
    }
    try {
      const fresh = await api.listGames({ light: true });
      setGames(fresh);
      setSelectedGame((prev) => (prev ? (fresh?.find((g) => g.id === prev.id) || null) : prev));
      try {
        const light = fresh?.map((g) => ({ ...g, image: (g.image || "").startsWith("data:") ? "" : g.image }));
        window.localStorage?.setItem("ag7_games_cache", JSON.stringify(light));
      } catch (e) { console.warn("games cache write failed:", e); }
    } catch (e) { console.warn("live pricing catalog refresh failed:", e); }
  }, [setExchangeRate, setGames, setSelectedGame]);
  useLivePricing(refreshPricing);
  const {
    loadOrders, loadMoreAdminOrders, adminLoadMoreBusy, adminHasMoreOrders,
    newOrderBanner, setNewOrderBanner, newChatBanner, setNewChatBanner,
    completedNotif, handleAcknowledgeCompletion, acknowledgeCompletionOrders,
  } = useOrderPolling({
    user, activeRole, orders, setOrders, setGames, setSelectedGame,
    playChimeSound, playChatChime, setUser,
  });

  // F3B-6b: selección/credenciales/verificación/carrito → useShoppingFlow.
  const {
    selectedPackage, setSelectedPackage, pendingPkgs, setPendingPkgs,
    checkoutOpen, setCheckoutOpen, pendingVerifyPkg, setPendingVerifyPkg,
    verifyAcknowledged, setVerifyAcknowledged,
    playerId, setPlayerId, playerPassword, setPlayerPassword,
    playerServer, setPlayerServer, accessMethod, setAccessMethod,
    verifiedPlayerName, setVerifiedPlayerName,
    verifiedPlayerRegion, setVerifiedPlayerRegion,
    isVerifyingId, idVerificationStatus, setIdVerificationStatus,
    ffPendingConfirmation, setFfPendingConfirmation,
    credHistoryRefreshKey, setCredHistoryRefreshKey,
    handleVerifyPlayerId, handleAddToCart, handleRemoveFromCart,
  } = useShoppingFlow({ selectedGame, cart, setCart, showToast, effectiveUsd, usdToBs, exchangeRate, setExchangeRate });

  // F3B-6c: formulario de pago + wallet + submit → usePaymentSubmit (verbatim).
  const {
    showPaymentModal, setShowPaymentModal,
    clientCi, setClientCi, selectedPmId, setSelectedPmId,
    checkoutPmTab, setCheckoutPmTab,
    paidAmountBs, setPaidAmountBs, referenceNumber, setReferenceNumber,
    needsManualRef, setNeedsManualRef,
    proofFile, setProofFile, proofFileName, setProofFileName,
    useAccountCredit, setUseAccountCredit,
    submitting, validationErrors, setValidationErrors,
    remainingAfterCreditBs,
    paymentResultModal, setPaymentResultModal,
    lastOrderResult, setLastOrderResult,
    previewProofUrl, setPreviewProofUrl,
    handleSubmitPayment,
  } = usePaymentSubmit({
    user, setUser, orders, setOrders, cart, setCart, cartTotalBs,
    exchangeRate, currency, paymentMethods, showToast,
  });

  // F3B-6a: pago parcial extraído a features/checkout/usePartialPayment.
  const {
    completePartialOrder, setCompletePartialOrder,
    partialAmount, setPartialAmount, partialRef, setPartialRef,
    partialFile, setPartialFile, partialFileName, setPartialFileName,
    completingPartial, partialErrors,
    partialPmId, setPartialPmId,
    partialUseCredit, setPartialUseCredit,
    openCompletePartial, remainingForOrder, handleSubmitPartialPayment,
  } = usePartialPayment({ showToast, orders, setOrders, setUser, user, paymentMethods, exchangeRate });

  // ============ AUTH (extracted to /hooks/useAuth.js) ============
  const auth = useAuth({
    setUser, setActiveRole, setClientTab, setSelectedGame, setSelectedPackage,
    setOrders, showToast, loadOrders, referralCode: pendingReferralCode,
  });
  // Destructure for readability across the rest of App.js render logic.
  const {
    authMode, setAuthMode, authBusy,
    loginEmail, setLoginEmail, loginPassword, setLoginPassword,
    regEmail, setRegEmail, regPassword, setRegPassword,
    regCountry, setRegCountry, regPhone, setRegPhone,
    twoFAChallenge, setTwoFAChallenge, twoFACode, setTwoFACode, twoFABusy,
    showForgotModal, setShowForgotModal,
    forgotStep, setForgotStep, forgotCountry, setForgotCountry,
    forgotPhone, setForgotPhone, forgotPhoneE164,
    forgotCode, setForgotCode, forgotUsername,
    forgotNewPass, setForgotNewPass, forgotNewPass2, setForgotNewPass2, forgotBusy,
    twoFAEnabled, twoFASetup, setTwoFASetup, twoFASetupCode, setTwoFASetupCode, twoFAEnableBusy,
    twoFADisablePwd, setTwoFADisablePwd, twoFADisableBusy,
    pwdOld, setPwdOld, pwdNew, setPwdNew, pwdBusy,
    handleLogin, handleVerify2FA,
    closeForgot, handleForgotStep1, handleForgotStep2, handleForgotStep3,
    handleRegister,
    loadTwoFAStatus, handleStartTwoFASetup, handleEnableTwoFA, handleDisableTwoFA,
    handleChangePassword, handleLogout,
  } = auth;


  useEffect(() => {
    loadPublicData();
  }, [loadPublicData]);

  useEffect(() => { captureReferralFromUrl(); }, []);

  const loadReferralSummary = useCallback(async () => {
    if (!user || user.role === "admin") return;
    setReferralLoading(true);
    try {
      const data = await api.getReferralSummary();
      setReferralData({ ...data, referral_link: `${window.location.origin}/?ref=${data.referral_code}` });
    } catch (err) { console.warn("referral summary failed:", err); }
    finally { setReferralLoading(false); }
  }, [user]);

  useEffect(() => { loadReferralSummary(); }, [loadReferralSummary]);

  useEffect(() => {
    const current = Number(referralData?.rewards_bs || 0);
    const previous = previousReferralRewardsRef.current;
    previousReferralRewardsRef.current = current;
    if (previous === null || current <= previous) return;
    setReferralRewardPulse(true);
    showToast("Recompensa acreditada", `Recibiste Bs ${(current - previous).toFixed(2)} por un referido.`, "success");
    const timeout = window.setTimeout(() => setReferralRewardPulse(false), 2600);
    return () => window.clearTimeout(timeout);
  }, [referralData?.rewards_bs, showToast]);

  useEffect(() => {
    const code = pendingReferralCode();
    if (!user || user.role === "admin" || !code) return;
    api.claimReferral(code)
      .then(() => { clearPendingReferralCode(); loadReferralSummary(); })
      .catch((err) => { console.warn("referral claim skipped:", err.message); clearPendingReferralCode(); });
  }, [user?.id, user?.role, loadReferralSummary]);

  const copyReferralLink = async () => {
    if (!referralData?.referral_link) return;
    try {
      await navigator.clipboard.writeText(referralData.referral_link);
      showToast("Enlace copiado", "Compártelo con tus amigos para ganar saldo.", "success");
    } catch (err) { showToast("No se pudo copiar", "Selecciona y copia el enlace manualmente.", "error"); }
  };
  const shareReferralLink = async () => {
    if (!referralData?.referral_link) return;
    try {
      if (navigator.share) await navigator.share({ title: "AG7 Store", text: "Únete a AG7 Store con mi enlace", url: referralData.referral_link });
      else await copyReferralLink();
    } catch (err) { if (err?.name !== "AbortError") console.warn("referral share failed:", err); }
  };

  useEffect(() => {
    // Try to hydrate the session from the httpOnly cookie by calling /me.
    // If it 401s we're anonymous — swallow and continue as a guest.
    // On any OTHER failure (502/504/network) we DO NOT log the user out:
    // we restore the last known user from sessionStorage so a transient
    // ingress hiccup doesn't bounce them back to the login screen.
    (async () => {
      try {
        const u = await api.me();
        setUser(u);
        setActiveRole(u.role === "admin" ? "admin" : "client");
        try { window.sessionStorage?.setItem("ag7_last_user", JSON.stringify(u)); }
        catch (e) { console.warn("cache last user failed:", e); }
        await loadOrders();
      } catch (e) {
        if (e.status === 401) {
          // Truly anonymous — clear the cached user so we don't rehydrate
          // a stale identity on the next mount.
          try { window.sessionStorage?.removeItem("ag7_last_user"); }
          catch (err) { console.warn("clear cached user failed:", err); }
        } else {
          console.error("Session hydrate failed (transient) — restoring cached user", e);
          try {
            const raw = window.sessionStorage?.getItem("ag7_last_user");
            const cached = raw ? JSON.parse(raw) : null;
            if (cached && cached.id) {
              setUser(cached);
              setActiveRole(cached.role === "admin" ? "admin" : "client");
              // Best-effort refresh of orders; failures are tolerated.
              await loadOrders();
            }
          } catch (err) { console.warn("restore cached user failed:", err); }
        }
      } finally {
        setAuthLoading(false);
      }
    })();
  }, [loadOrders]);

  // First-login welcome modal for clients. Fires once per user (persisted
  // server-side via has_seen_welcome + a localStorage backup so it also survives
  // a stale JWT/user object).
  useEffect(() => {
    if (!user || user.role === "admin") return;
    const localKey = `ag7_welcome_seen_${user.id}`;
    const localSeen = typeof window !== "undefined" && window.localStorage
      ? window.localStorage.getItem(localKey) === "1"
      : false;
    if (!user.has_seen_welcome && !localSeen) {
      // Small delay so the login toast doesn't overlap the modal entrance.
      const t = setTimeout(() => setShowWelcomeModal(true), 800);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [user]);






  // ============ Order chat (client↔admin) state ============
  const [chatOrder, setChatOrder] = useState(null); // the order whose chat is open




  // ============ ADMIN: PACKAGES & GAMES ============
  // P4B-1/P4B-2 (F3): todo el CRUD de paquetes y juegos (forms, imágenes,
  // verificationGuide) vive ahora en features/admin/useAdminCatalog.js.

  // ============ ADMIN: BANKS ============


  useEffect(() => {
    if (activeRole === "admin" && adminTab === "usuarios") {
      loadUsers();
    }
  }, [activeRole, adminTab, loadUsers]);

  useEffect(() => {
    if (activeRole === "admin" && adminTab === "seguridad") {
      loadTwoFAStatus();
      loadSiteStatus();
    }
  }, [activeRole, adminTab, loadTwoFAStatus, loadSiteStatus]);

  // Load site status once on mount + light poll (60s) so clients see closures.
  // También se refresca al volver a la pestaña y cuando el backend rechaza un
  // pedido por tienda cerrada (evento "ag7:site-status-changed").
  useEffect(() => {
    loadSiteStatus();
    const t = setInterval(loadSiteStatus, 60000);
    const onVisible = () => { if (document.visibilityState === "visible") loadSiteStatus(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("ag7:site-status-changed", loadSiteStatus);
    return () => {
      clearInterval(t);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("ag7:site-status-changed", loadSiteStatus);
    };
  }, [loadSiteStatus]);

  // Heartbeat de actividad REAL del admin (cierre automático nocturno).
  useAdminActivity(activeRole === "admin");

  useEffect(() => {
    if (activeRole === "admin" && adminTab === "redes") {
      loadAdminSocialNets();
    }
  }, [activeRole, adminTab, loadAdminSocialNets]);

  // When admin opens the "Cuentas" tab, load ALL payment methods (including inactive).
  useEffect(() => {
    if (activeRole === "admin" && adminTab === "pagos") {
      api.adminListPaymentMethods().then(setPaymentMethods).catch(() => {});
    }
  }, [activeRole, adminTab]);

  useEffect(() => {
    if (activeRole === "admin" && adminTab === "banners") {
      loadBanners();
    }
  }, [activeRole, adminTab, loadBanners]);

  // Admin catalog editor needs the FULL games payload (with base64 package
  // images). Home fetches the light version for speed, so when admin opens
  // the "Paquetes" tab we upgrade the state to the heavy version.
  useEffect(() => {
    if (activeRole !== "admin" || adminTab !== "paquetes") return;
    (async () => {
      try {
        const full = await api.listGames();
        setGames(full);
      } catch (e) { console.warn("full games load (admin paquetes) failed:", e); }
    })();
  }, [activeRole, adminTab]);

  // Client-side: fetch the active banner whenever the user picks a game
  useEffect(() => {
    if (activeRole === "client" && selectedGame?.id) {
      loadActiveGameBanner(selectedGame.id);
    } else {
      setActiveGameBanner(null);
    }
  }, [activeRole, selectedGame?.id, loadActiveGameBanner]);

  // Reset any pending package selections whenever the user switches to a different game
  useEffect(() => {
    setPendingPkgs([]);
    setCheckoutOpen(false);
  }, [selectedGame?.id]);

  // Load public list of social networks on user login (used by the completion modal)
  useEffect(() => {
    if (user) loadSocialNetworksPublic();
  }, [user, loadSocialNetworksPublic]);

  // ============ ADMIN: EXCHANGE RATE ============
  const [rateInput, setRateInput] = useState("");
  useEffect(() => { setRateInput(String(exchangeRate)); }, [exchangeRate]);

  const [supportWhatsappInput, setSupportWhatsappInput] = useState("");
  const [savingSupportWa, setSavingSupportWa] = useState(false);
  useEffect(() => { setSupportWhatsappInput(supportWhatsapp); }, [supportWhatsapp]);

  const handleSaveSupportWhatsapp = async () => {
    setSavingSupportWa(true);
    try {
      const res = await api.updateSupportWhatsapp(supportWhatsappInput);
      setSupportWhatsapp(res.support_whatsapp);
      showToast("WhatsApp guardado", `Los reportes se enviarán a +${res.support_whatsapp}`, "success");
    } catch (err) {
      showToast("Error", err.message || "No se pudo guardar", "error");
    } finally {
      setSavingSupportWa(false);
    }
  };
  const handleSaveRate = async () => {
    const v = parseFloat(rateInput);
    if (isNaN(v) || v <= 0) { showToast("Error", "Tasa inválida", "error"); return; }
    try {
      const res = await api.updateExchangeRate(v);
      const newRate = Number(res?.exchange_rate);
      if (Number.isFinite(newRate) && newRate > 0) {
        setExchangeRate(newRate);
        try { window.localStorage?.setItem("ag7_exchange_rate", String(newRate)); }
        catch (e) { console.warn("exchange_rate write failed:", e); }
      }
      showToast("Tasa Actualizada", `Nueva tasa: ${newRate.toFixed(2)} Bs`, "success");
    } catch (err) {
      showToast("Error", err.message, "error");
    }
  };

  // ============ PLAYER ID VERIFY ============



  // ============ SUPPORT (WhatsApp) ============
  const openSupportModal = () => {
    // Auto-fill order/UID field with the most useful piece of context
    let ctx = "";
    if (orders && orders.length > 0) {
      ctx = orders[0].id;
    } else if (playerId) {
      ctx = `UID: ${playerId}`;
    }
    setSupportOrderId(ctx);
    setSupportDesc("");
    setSupportFile(null);
    setShowSupportModal(true);
  };

  const handleSubmitSupport = async (e) => {
    e.preventDefault();
    const desc = supportDesc.trim();
    if (desc.length < 5) {
      showToast("Descripción muy corta", "Escribe al menos 5 caracteres explicando el problema.", "error");
      return;
    }
    if (supportFile && supportFile.size > 10 * 1024 * 1024) {
      showToast("Imagen muy grande", "La captura no puede superar 10 MB.", "error");
      return;
    }
    setSupportSubmitting(true);
    try {
      let imageUrl = "";
      if (supportFile) {
        const uploaded = await api.uploadSupportScreenshot(supportFile);
        imageUrl = uploaded.url;
      }
      const orderPart = supportOrderId.trim();
      const message =
        "⚠️ REPORTES DE SOPORTE - AG7 STORE\n\n" +
        `📌 Descripcion del problema: ${desc}\n\n` +
        `🖼️ Captura del error: ${imageUrl || "(sin captura adjunta)"}\n\n` +
        `🆔 Pedido/UID afectado: ${orderPart || "(no aplica)"}\n\n` +
        `— Enviado por ${user?.email || "cliente"}`;
      const waUrl = `https://api.whatsapp.com/send/?phone=${supportWhatsapp}&text=${encodeURIComponent(message)}`;
      window.open(waUrl, "_blank", "noopener,noreferrer");
      showToast("Reporte enviado", "Se abrió WhatsApp con tu mensaje", "success");
      setShowSupportModal(false);
      setSupportDesc("");
      setSupportFile(null);
    } catch (err) {
      showToast("Error al subir captura", err.message || "Intenta nuevamente", "error");
    } finally {
      setSupportSubmitting(false);
    }
  };

  // ============ ADMIN: UPDATE ORDER STATUS ============
  const handleUpdateOrderStatus = async (orderId, newStatus) => {
    try {
      const updated = await api.updateOrderStatus(orderId, newStatus);
      setOrders(orders.map(o => o.id === orderId ? updated : o));
      showToast("Estado Actualizado", `Pedido ${orderId}: ${newStatus}`, "success");
    } catch (err) {
      showToast("Error", err.message, "error");
    }
  };

  const handleReopenOrder = async (orderId) => {
    const ok = window.confirm(
      "¿Reabrir este pedido? El estado volverá a \"En Proceso\" y se reactivará el chat con el cliente."
    );
    if (!ok) return;
    try {
      const updated = await api.updateOrderStatus(orderId, "En Proceso");
      setOrders(orders.map(o => o.id === orderId ? updated : o));
      showToast("Pedido Reabierto", "Chat reactivado. Ahora puedes seguir hablando con el cliente.", "success");
    } catch (err) {
      showToast("Error", err.message, "error");
    }
  };

  const handleConfirmRecordedDelivery = async (order) => {
    const orderNumber = order?.orderNumber;
    if (orderNumber == null) {
      showToast("No se puede finalizar", "El pedido no tiene un número de referencia seguro.", "error");
      return;
    }
    const confirmed = window.confirm(
      `¿Confirmas que el cliente recibió la recarga del pedido #${String(orderNumber).padStart(4, "0")}? Esta acción cerrará el pedido sin reenviar una recarga.`
    );
    if (!confirmed) return;
    try {
      const updated = await api.confirmRecordedDeliveryByOrderNumber(orderNumber);
      setOrders(orders.map((item) => item.orderNumber === orderNumber ? updated : item));
      showToast("Entrega confirmada", `Pedido #${String(orderNumber).padStart(4, "0")} finalizado de forma segura.`, "success");
    } catch (err) {
      showToast("No se pudo finalizar", err.message, "error");
    }
  };

  const handleApprovePayment = async (orderId) => {
    try {
      const updated = await api.approvePayment(orderId);
      setOrders(orders.map(o => o.id === orderId ? updated : o));
      showToast("Pago Aprobado", `Comprobante validado manualmente`, "success");
    } catch (err) {
      showToast("Error", err.message, "error");
    }
  };

  const openRejectOrder = (order) => {
    setRejectReason("");
    setReclaimSurplusOnReject(true);
    setRejectOrderModal(order);
  };

  const handleConfirmRejectOrder = async () => {
    if (!rejectOrderModal) return;
    const reason = rejectReason.trim();
    if (reason.length < 5) {
      showToast("Motivo requerido", "Escribe un motivo claro (mín. 5 caracteres).", "error");
      return;
    }
    setRejectingOrder(true);
    try {
      const updated = await api.updateOrderStatus(
        rejectOrderModal.id, "Rechazado", reason,
        { reclaimSurplus: reclaimSurplusOnReject },
      );
      setOrders(orders.map(o => o.id === updated.id ? updated : o));
      const surplusHint = Number(rejectOrderModal.surplusBs) > 0.01
        && rejectOrderModal.surplusCredited === true && reclaimSurplusOnReject
        ? ` · Saldo a favor revocado (Bs ${Number(rejectOrderModal.surplusBs).toFixed(2)}).`
        : "";
      showToast(
        "Pedido Rechazado",
        `El cliente verá el motivo: "${reason.slice(0, 40)}${reason.length > 40 ? "..." : ""}"${surplusHint}`,
        "success"
      );
      setRejectOrderModal(null);
      setRejectReason("");
    } catch (err) {
      showToast("Error", err.message, "error");
    } finally {
      setRejectingOrder(false);
    }
  };

  // Admin: approve the client's difference-payment proof for an adjusted order
  // and flip it to Completado. The order's package snapshot has already been
  // switched by request_order_adjustment on the backend; here we just verify
  // the incoming proof and close the loop.
  const handleApproveAdjustment = async (orderId) => {
    try {
      const updated = await api.approveOrderAdjustment(orderId);
      setOrders((prev) => prev?.map(o => o.id === updated.id ? updated : o));
      showToast("Diferencia Aprobada", "Pago aprobado. El pedido quedó En Proceso: pulsa \"Recarga realizada\" cuando completes la recarga.", "success");
    } catch (err) {
      showToast("Error", err.message || "No se pudo aprobar el ajuste", "error");
    }
  };

  // ============ COMPUTED METRICS ============
  const totalRevenueUsd = orders.reduce((acc, o) => acc + (o.priceUsd || 0), 0);
  const pendingOrdersCount = orders.filter(o => o.status === "Pendiente").length;
  const processingOrdersCount = orders.filter(o => o.status === "En Proceso").length;
  const incompletePaymentOrdersCount = orders.filter(o => o.status === "Pago Incompleto").length;
  const completedOrdersCount = orders.filter(o => o.status === "Completado").length;
  const needsReviewOrdersCount = orders.filter(o => o.verification_status === "NEEDS_REVIEW" && !["Completado", "Rechazado"].includes(o.status)).length;

  // ─── Admin toast: alert when NEEDS_REVIEW backlog exists at login ─────
  // Fires ONCE per admin session (guarded by a ref) so a growing list of
  // pending manual reviews doesn't spam toasts on every render.
  React.useEffect(() => {
    if (user?.role !== "admin" || activeRole !== "admin") return;
    if (needsReviewToastShownRef.current) return;
    if (needsReviewOrdersCount <= 0) return;
    needsReviewToastShownRef.current = true;
    showToast(
      "Comprobantes por verificar",
      `${needsReviewOrdersCount} pedido${needsReviewOrdersCount === 1 ? "" : "s"} requiere${needsReviewOrdersCount === 1 ? "" : "n"} revisión manual.`,
      "warning"
    );
  }, [user?.role, activeRole, needsReviewOrdersCount, showToast]);

  const canSwitchAdmin = !!user && user.role === "admin";

  // ============ F3B-7: React Router — sincronización URL ↔ estado de navegación ============
  // La navegación interna sigue siendo por estado (activeRole/clientTab/adminTab/
  // selectedGame); esta capa la refleja en URLs reales y viceversa (deep-link,
  // refresh, Back/Forward) sin tocar los handlers ni la UI existentes.
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();

  // Scroll-to-top on route change (PUSH/REPLACE). POP (back/forward) keeps
  // the browser's native scroll restoration. In-page scrolls (hero CTA →
  // #ag7-games) don't change the pathname so they are unaffected.
  useEffect(() => {
    if (navigationType !== "POP") window.scrollTo(0, 0);
  }, [location.pathname, navigationType]);

  // Path que representa el estado de navegación actual.
  const pathFromState = (() => {
    if (activeRole === "admin" && canSwitchAdmin) return `/admin/${adminTab}`;
    if (clientTab === "cart") return "/carrito";
    if (clientTab === "orders") return "/pedidos";
    if (clientTab === "wallet") return "/billetera";
    if (clientTab === "referrals") return "/referidos";
    if (clientTab === "tournaments") return "/torneos";
    if (clientTab === "store" && selectedGame?.slug) return `/juego/${selectedGame.slug}`;
    return "/";
  })();

  // URL ↔ estado — un único efecto decide la dirección de sincronización:
  // • URL cambió (Back/Forward/deep-link/refresh) → se aplica al estado.
  // • Estado cambió (clicks existentes con setState) → se refleja en la URL.
  // • Tras login con URL significativa (≠ "/") gana la URL (deep-link);
  //   con "/" gana el estado (p.ej. admin aterriza en /admin/dashboard).
  const syncedPathRef = React.useRef(null);      // última URL aplicada/aceptada
  const lastStatePathRef = React.useRef(null);   // último pathFromState visto
  const prevUserRef = React.useRef(null);
  useEffect(() => {
    const path = location.pathname;
    const wasLoggedOut = !prevUserRef.current;
    prevUserRef.current = user;
    if (!user) { syncedPathRef.current = path; lastStatePathRef.current = pathFromState; return; }
    const stateChanged = lastStatePathRef.current !== pathFromState;
    lastStatePathRef.current = pathFromState;
    if (path === pathFromState) { syncedPathRef.current = path; return; }
    const urlChanged = path !== syncedPathRef.current || (wasLoggedOut && path !== "/");
    if (urlChanged) {
      const prevSynced = syncedPathRef.current;
      syncedPathRef.current = path;
      const adminMatch = matchPath("/admin/:tab", path);
      const gameMatch = matchPath("/juego/:slug", path);
      if (adminMatch) {
        if (!canSwitchAdmin) { navigate("/", { replace: true }); return; }
        const rawTab = adminMatch.params.tab;
        const tab = ADMIN_TAB_IDS.includes(rawTab) ? rawTab : "dashboard";
        setActiveRole("admin");
        setAdminTab(tab);
        if (rawTab !== tab) navigate(`/admin/${tab}`, { replace: true });
        return;
      }
      if (activeRole !== "client") setActiveRole("client");
      if (gameMatch) {
        setClientTab("store");
        const g = games?.find(x => x.slug === gameMatch.params.slug);
        if (g) {
          if (selectedGame !== g) setSelectedGame(g);
        } else if (gamesLoaded) {
          navigate("/", { replace: true }); // slug inexistente una vez cargado el catálogo
        } else {
          syncedPathRef.current = prevSynced; // catálogo aún no cargado — reintentar cuando llegue
        }
        return;
      }
      if (path === "/carrito") { setClientTab("cart"); return; }
      if (path === "/pedidos") { setClientTab("orders"); return; }
      if (path === "/billetera") { setClientTab("wallet"); return; }
      if (path === "/referidos") { setClientTab("referrals"); return; }
      if (path === "/torneos") { setClientTab("tournaments"); return; }
      setClientTab("store");
      if (selectedGame) setSelectedGame(null);
      if (path !== "/") navigate("/", { replace: true });
      return;
    }
    if (stateChanged) navigate(pathFromState);
    // El estado de navegación se lee del render actual; los refs evitan bucles.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, pathFromState, user, games, gamesLoaded, canSwitchAdmin]);


  // PASO 1 (F3B): ambos context bags memoizados. La lista de deps usa
  // Object.values(bag) — longitud constante (keys fijas) — de modo que el
  // memo solo se recomputa cuando ALGUN valor cambia. Cuando los handlers
  // migren a useCallback (fases siguientes) esto elimina re-renders de los
  // consumidores del contexto.
  const authCtxBag = {
      toast, publicStats, authMode, setAuthMode, authBusy,
      loginEmail, setLoginEmail, loginPassword, setLoginPassword, handleLogin,
      regEmail, setRegEmail, regPassword, setRegPassword,
      regCountry, setRegCountry, regPhone, setRegPhone, handleRegister,
      twoFAChallenge, setTwoFAChallenge, twoFACode, setTwoFACode,
      twoFABusy, handleVerify2FA,
      showForgotModal, setShowForgotModal, forgotStep, setForgotStep,
      forgotCountry, setForgotCountry, forgotPhone, setForgotPhone,
      forgotPhoneE164,
      forgotCode, setForgotCode, forgotUsername,
      forgotNewPass, setForgotNewPass, forgotNewPass2, setForgotNewPass2,
      forgotBusy, closeForgot, handleForgotStep1, handleForgotStep2, handleForgotStep3,
    };;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const authCtx = useMemo(() => authCtxBag, Object.values(authCtxBag));

  const appCtxBag = {
    // Core
    user, adminTab, setAdminTab, showToast,
    // Role — MUST be exposed so AppModals + SocialProofFeed can gate
    // client-only UI (FAB de soporte, live-feed toasts, welcome modal…).
    // Its absence silently hid the WhatsApp support FAB from the client
    // view because `activeRole === "client"` evaluated as `undefined ===
    // "client"` → false.
    activeRole, setActiveRole,
    // Dashboard metrics
    totalRevenueUsd, pendingOrdersCount, processingOrdersCount,
    completedOrdersCount, needsReviewOrdersCount,
    incompletePaymentOrdersCount,
    // Format / conversion helpers
    exchangeRate, setExchangeRate, rateReady, usdToBs, formatBs, copyToClipboard,
    // Orders admin
    orders, setOrders, orderFilter, setOrderFilter,
    handleApprovePayment, handleUpdateOrderStatus, handleReopenOrder, handleConfirmRecordedDelivery,
    openRejectOrder, setPreviewProofUrl, setChatOrder,
    // Games / packages admin
    games, setGames, expandedGames, toggleExpandedGame,
    openCreateGameModal, openEditGameModal, setDeleteGameConfirm,
    handleToggleGameActive, handleUpdateGameCredType,
    setShowAddPkgModal, setEditingPkg, resetPkgForm,
    setPkgGameId, setPkgNameInput, setPkgPriceUsdInput,
    setPkgDescriptionInput, setPkgImageInput,
    setPkgResellerPriceUsdInput, setPkgRequiresVerify,
    setPkgIsBestSellerInput, setPkgVerifyTitle,
    setPkgVerifyDescription, setPkgVerifyImageUrl, setPkgVerifyStepsText,
    handleDeletePackage,
    // Payment methods (Cuentas)
    paymentMethods, handleTogglePmActive, handleDeleteBank,
    setShowAddBankModal, openEditPm, setBankForm, setEditingPm,
    EMPTY_BANK_FORM,
    // Users
    users, loadingUsers, loadUsers, userRoleFilter, setUserRoleFilter,
    userSearch, setUserSearch, requestStatusChange,
    handleToggleUserRole, setDeleteUserConfirm, openClientHistory,
    handleChangeUserStatus,
    // Divisa & soporte
    rateInput, setRateInput, handleSaveRate,
    supportWhatsapp, supportWhatsappInput, setSupportWhatsappInput,
    handleSaveSupportWhatsapp, savingSupportWa,
    // Social networks
    adminSocialNets, snForm, setSnForm, snBusy, handleSaveSocial,
    handleToggleSocial, handleDeleteSocial,
    // Banners
    banners, bannerForm, setBannerForm, bannerBusy,
    handleBannerImageUpload, bannerUploadingIdx, handleRemoveBannerImage,
    handleSaveBanner, handleToggleBannerActive, handleDeleteBanner,
    resetBannerForm,
    // Seguridad
    siteStatus, siteStatusForm, setSiteStatusForm, siteStatusBusy,
    handleToggleSiteOpen, handleSaveClosureReason, loadSiteStatus,
    twoFAEnabled, twoFASetup, twoFASetupCode, setTwoFASetupCode,
    twoFAEnableBusy, handleStartTwoFASetup, handleEnableTwoFA,
    setTwoFASetup, twoFADisablePwd, setTwoFADisablePwd,
    twoFADisableBusy, handleDisableTwoFA,
    pwdOld, setPwdOld, pwdNew, setPwdNew, pwdBusy, handleChangePassword,
    // AppModals extras
    currency, setUser, socialNetworks, tour,
    referralData, referralLoading, copyReferralLink, shareReferralLink,
    formatUsd, formatItemMoney,
    cart, cartTotalBs, cartTotalUsd, remainingAfterCreditBs,
    showPaymentModal, setShowPaymentModal, submitting,
    selectedPmId, setSelectedPmId, checkoutPmTab, setCheckoutPmTab,
    paidAmountBs, setPaidAmountBs, referenceNumber, setReferenceNumber,
    needsManualRef, setNeedsManualRef, proofFile, setProofFile,
    proofFileName, setProofFileName, useAccountCredit, setUseAccountCredit,
    validationErrors, handleSubmitPayment,
    paymentResultModal, setPaymentResultModal, lastOrderResult,
    completePartialOrder, setCompletePartialOrder,
    partialAmount, setPartialAmount, partialRef, setPartialRef,
    partialFile, setPartialFile, partialFileName, setPartialFileName,
    completingPartial, partialErrors, handleSubmitPartialPayment, remainingForOrder,
    partialPmId, setPartialPmId,
    partialUseCredit, setPartialUseCredit,
    previewProofUrl,
    showGameModal, setShowGameModal, editingGame, gameForm, setGameForm,
    savingGame, handleSaveGame, handleGameImageFile,
    deleteGameConfirm, handleConfirmDeleteGame,
    showAddPkgModal, editingPkg, pkgGameId, pkgNameInput, pkgPriceUsdInput,
    pkgDescriptionInput, pkgImageInput, pkgResellerPriceUsdInput, pkgCostPriceUsdInput,
    setPkgCostPriceUsdInput, pkgRequiresVerify,
    pkgIsBestSellerInput, pkgVerifyTitle, pkgVerifyDescription, pkgVerifyImageUrl,
    pkgVerifyStepsText, handleSavePackage, handlePkgImageFile, handlePkgVerifyImageFile,
    showAddBankModal, bankForm, editingPm, handleAddBank,
    chatOrder,
    showWelcomeModal, setShowWelcomeModal,
    clientHistory, setClientHistory, clientHistoryLoading,
    rejectOrderModal, setRejectOrderModal, rejectReason, setRejectReason, rejectingOrder,
    handleConfirmRejectOrder,
    reclaimSurplusOnReject, setReclaimSurplusOnReject,
    // Order adjustment workflow
    adjustOrderModal, setAdjustOrderModal,
    adjustPayOrder, setAdjustPayOrder,
    handleApproveAdjustment,
    // Manual override modal for NEEDS_REVIEW orders
    manualVerifyOrder, setManualVerifyOrder,
    deleteUserConfirm, handleDeleteUser,
    statusChangeModal, setStatusChangeModal, statusChangeReason, setStatusChangeReason,
    showSupportModal, setShowSupportModal, supportDesc, setSupportDesc,
    supportFile, setSupportFile, supportOrderId, setSupportOrderId,
    supportSubmitting, handleSubmitSupport,
    completedNotif, handleAcknowledgeCompletion, acknowledgeCompletionOrders,
    newOrderBanner, setNewOrderBanner, newChatBanner, setNewChatBanner,
    setClientTab, loadOrders, openSupportModal, playChimeSound,
    // StoreDetailPage
    selectedGame, setSelectedGame,
    pendingPkgs, setPendingPkgs,
    checkoutOpen, setCheckoutOpen,
    playerId, setPlayerId, playerPassword, setPlayerPassword,
    playerServer, setPlayerServer, accessMethod, setAccessMethod,
    verifiedPlayerName, setVerifiedPlayerName,
    verifiedPlayerRegion, setVerifiedPlayerRegion,
    ffPendingConfirmation, setFfPendingConfirmation,
    isVerifyingId, idVerificationStatus, setIdVerificationStatus,
    handleVerifyPlayerId, handleAddToCart,
    pendingVerifyPkg, setPendingVerifyPkg,
    verifyAcknowledged, setVerifyAcknowledged,
    isReseller, effectiveUsd, formatMoney,
    activeGameBanner,
    credHistoryRefreshKey,
    loadMoreAdminOrders, adminLoadMoreBusy, adminHasMoreOrders,
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const appCtx = useMemo(() => appCtxBag, Object.values(appCtxBag));

  // ============================================================
  // RENDER: LOADING
  // ============================================================
  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#06070b] text-white flex items-center justify-center">
        <div className="text-center space-y-3">
          <RefreshCw className="w-8 h-8 text-purple-400 animate-spin mx-auto" />
          <p className="text-xs font-mono text-zinc-400">CARGANDO AG7 STORE...</p>
        </div>
      </div>
    );
  }

  // ============================================================
  // RENDER: AUTH SCREEN
  // ============================================================
  if (!user) {
    return (
      <AppProvider value={authCtx}>
        <AuthPage />
      </AppProvider>
    );
  }

  // ============================================================
  // RENDER: MAIN APP
  // ============================================================

  // Context bag consumed by the extracted page components (AdminPage, etc.).
  // Keeping this inline (vs a useMemo) is deliberate: React re-renders App.js
  // whenever any of these values change anyway, so memoising the bag would
  // NOT prevent context consumers from re-rendering — it would only add
  // maintenance overhead.


  return (
    <AppProvider value={appCtx}>
    <div className="min-h-screen bg-[#06070b] text-[#f4f4f5] font-sans pb-16 selection:bg-purple-500 selection:text-white">
      {toast && <Toast toast={toast} />}


      {/* Header */}
      <header className="border-b border-[var(--ag7-border)] bg-[#09090b]/85 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 h-14 sm:h-16 flex items-center justify-between gap-2">
          <div data-testid="app-div-1" className="flex items-center gap-2 cursor-pointer min-w-0" onClick={() => { setActiveRole("client"); setClientTab("store"); setSelectedGame(null); }}>
            <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-xl overflow-hidden border border-ag7-purple/60 bg-zinc-950 p-0.5 shadow-[0_0_16px_rgba(147,51,234,0.45)] flex-shrink-0">
              <img src={LOGO_URL} alt="Logo" className="w-full h-full object-cover rounded-lg" />
            </div>
            <div className="min-w-0">
              <h1 className="font-heading font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-500 text-sm sm:text-lg tracking-wider truncate">AG7 STORE</h1>
              <p className="text-[8px] sm:text-[9px] text-zinc-400 tracking-[0.15em] sm:tracking-[0.2em] font-mono hidden xs:block">RECARGAS PREMIUM</p>
            </div>
          </div>

          {/* Store status pill — reflects admin's Open/Closed toggle in real time */}
          <div
            data-testid="header-store-status"
            className={`hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[10px] font-mono font-bold uppercase tracking-wider ${
              siteStatus.open
                ? "bg-green-500/10 border-green-500/40 text-green-300"
                : "bg-red-500/10 border-red-500/40 text-red-300"
            }`}
            title={siteStatus.open ? "Procesando pedidos ahora" : (siteStatus.reason || "Fuera de horario")}
          >
            <span className={`relative flex w-2 h-2`}>
              {siteStatus.open && (
                <span className="absolute inline-flex w-full h-full rounded-full bg-green-400 opacity-70 animate-ping"></span>
              )}
              <span className={`relative inline-flex w-2 h-2 rounded-full ${siteStatus.open ? "bg-green-400" : "bg-red-400"}`}></span>
            </span>
            <span className="hidden md:inline">{siteStatus.open ? "En línea · Procesando" : "Fuera de horario"}</span>
            <span className="md:hidden">{siteStatus.open ? "En línea" : "Cerrado"}</span>
          </div>

          <div className="flex items-center gap-1.5 sm:gap-3">
            {/* Persistent cart button (client view only) */}
            {activeRole === "client" && (
              <button
                onClick={() => setClientTab("cart")}
                data-testid="header-cart-btn"
                className={`relative flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-xl transition-all border ${
                  cart.length > 0
                    ? "bg-gradient-to-r from-green-600 to-emerald-500 border-green-400 text-white shadow-md shadow-green-500/30 animate-pulse"
                    : "bg-ag7-surface-2/80 border-[var(--ag7-border)] text-zinc-400 hover:text-white hover:border-ag7-purple/50"
                }`}>
                <ShoppingCart className="w-4 h-4 sm:w-5 sm:h-5" />
                {cart.length > 0 && (
                  <>
                    <span data-testid="header-cart-count"
                      className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 text-white text-[10px] font-black flex items-center justify-center border-2 border-[#06070b]">
                      {cart.length}
                    </span>
                    <span className="hidden sm:inline text-xs font-black font-mono" data-testid="header-cart-total">
                      {currency === "USD" ? formatUsd(cartTotalUsd) : formatBs(cartTotalBs)}
                    </span>
                  </>
                )}
              </button>
            )}

            {/* Mobile-only compact "Saldo a favor" badge — always visible in
                the header so users never lose sight of their balance on
                narrow viewports (desktop shows the full WalletChip in the
                tabs bar). */}
            {activeRole === "client" && (
              <HeaderWalletBadge data-testid="app-header-wallet-badge-2"
                amount={user.credit_balance_bs || 0}
                rewardPulse={referralRewardPulse}
                onClick={() => setClientTab("wallet")}
              />
            )}

            {user.role === "admin" && (
              <div
                data-testid="admin-rate-badge"
                title={rateReady ? undefined : "Consultando tasa oficial..."}
                className={`hidden md:flex items-center gap-2 bg-zinc-900/90 border border-zinc-800 px-3 py-1.5 rounded-full text-xs font-mono ${rateReady ? "text-cyan-400" : "text-zinc-500"}`}
              >
                <span className={`w-2 h-2 rounded-full ${rateReady ? "bg-green-500" : "bg-amber-500"} animate-pulse`}></span>
                {rateReady ? (
                  <span>1 USD = <strong className="text-white">{exchangeRate.toFixed(2)} Bs</strong></span>
                ) : (
                  <span className="inline-flex items-center gap-2">
                    <span>1 USD =</span>
                    <span
                      data-testid="admin-rate-skeleton"
                      className="inline-block h-3 w-16 rounded bg-zinc-700/70 animate-pulse"
                    />
                  </span>
                )}
              </div>
            )}

            {activeRole === "client" && (
              <div
                data-testid="currency-switch"
                role="group"
                aria-label="Cambiar moneda"
                className="inline-flex items-center rounded-xl bg-ag7-surface-2/80 border border-[var(--ag7-border)] p-0.5 text-xs font-mono"
              >
                <button
                  type="button"
                  onClick={() => setCurrency("BS")}
                  data-testid="currency-switch-bs"
                  className={`px-2.5 py-1.5 rounded-lg transition-all ${currency === "BS" ? "bg-cyan-500/20 text-cyan-200 border border-cyan-400/40 shadow-inner" : "text-zinc-400 hover:text-white"}`}
                  aria-pressed={currency === "BS"}
                  title="Ver precios en Bolívares"
                >Bs</button>
                <button
                  type="button"
                  onClick={() => { if (currency !== "USD") setConfirmUsdSwitch(true); }}
                  data-testid="currency-switch-usd"
                  className={`px-2.5 py-1.5 rounded-lg transition-all ${currency === "USD" ? "bg-green-500/25 text-green-200 border border-green-400/50 shadow-inner" : "text-zinc-400 hover:text-white"}`}
                  aria-pressed={currency === "USD"}
                  title="Ver precios en Dólares"
                >USD</button>
              </div>
            )}

            <div className="flex items-center gap-1.5 bg-zinc-900/90 border border-zinc-800 px-2 sm:px-3 py-1.5 rounded-xl text-xs">
              <User className="w-3.5 h-3.5 text-purple-400 flex-shrink-0" />
              <span className="font-bold text-white hidden md:inline max-w-[100px] truncate">{user.name}</span>
              <button onClick={handleLogout} data-testid="logout-btn"
                className="text-zinc-400 hover:text-red-400 flex items-center gap-1 md:border-l md:border-zinc-700 md:pl-2 md:ml-1" title="Cerrar Sesión">
                <LogOut className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-3 sm:px-4 mt-4 sm:mt-6">
        {/* ============ CLIENT PANEL ============ */}
        {activeRole === "client" && (
          <div className="space-y-6" data-testid="client-panel">
            {/* Aviso SÓLO para clientes con pedidos registrados aún no atendidos */}
            {!siteStatus.open && <AdminOfflineNotice orders={orders} />}
            {/* ============ SITE-CLOSED SPLASH (neon "CERRADO" banner) ============ */}
            {!siteStatus.open && (
              <div
                data-testid="site-closed-banner"
                className="relative overflow-hidden rounded-3xl bg-[#05060a] my-4"
              >
                {/* Ambient neon glow layers */}
                <div className="pointer-events-none absolute inset-0 opacity-70" aria-hidden="true">
                  <div className="absolute -top-24 -left-24 w-72 h-72 rounded-full bg-cyan-500/25 blur-3xl"></div>
                  <div className="absolute -bottom-24 -right-24 w-80 h-80 rounded-full bg-fuchsia-500/25 blur-3xl"></div>
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(120,60,255,0.15),transparent_60%)]"></div>
                </div>

                {/* Neon frame */}
                <div className="relative m-3 sm:m-5 rounded-2xl border-2 border-cyan-400/60 shadow-[0_0_35px_rgba(34,211,238,0.45),inset_0_0_25px_rgba(34,211,238,0.25)] p-6 sm:p-10 flex flex-col items-center text-center gap-6">
                  {/* Inner accent frame */}
                  <div className="pointer-events-none absolute inset-2 rounded-xl border border-fuchsia-500/40" aria-hidden="true"></div>

                  {/* AG7 Store logo — brand image, centered */}
                  <div className="relative flex justify-center">
                    <div className="absolute inset-0 -m-6 rounded-full bg-cyan-400/25 blur-3xl" aria-hidden="true"></div>
                    <img
                      src="/assets/ag7-logo.png"
                      alt="AG7 Store"
                      data-testid="site-closed-logo"
                      className="relative w-40 h-40 sm:w-52 sm:h-52 rounded-2xl object-cover shadow-[0_0_35px_rgba(34,211,238,0.55),0_0_60px_rgba(168,85,247,0.35)] ring-2 ring-cyan-400/50"
                      draggable={false}
                    />
                  </div>

                  {/* CERRADO sign — neon-red on dark wood-like plate, flickers like real neon */}
                  <div className="relative w-full max-w-xl mx-auto">
                    <div className="absolute -inset-1 rounded-2xl bg-red-500/25 blur-xl" aria-hidden="true"></div>
                    <div className="relative rounded-2xl border-4 border-red-500/70 bg-gradient-to-b from-[#3a1616] to-[#1a0808] py-5 sm:py-7 px-4 shadow-[0_0_45px_rgba(239,68,68,0.55),inset_0_0_25px_rgba(239,68,68,0.35)]">
                      <p
                        data-testid="site-closed-cerrado-text"
                        className="font-black text-4xl sm:text-6xl md:text-7xl tracking-[0.15em] text-red-300 animate-neon-flicker"
                        style={{
                          textShadow:
                            "0 0 8px #ff2a2a, 0 0 20px #ff2a2a, 0 0 40px #ff5c5c, 0 0 60px rgba(255, 92, 92, 0.6)",
                        }}
                      >
                        CERRADO
                      </p>
                      <p className="mt-2 text-xs sm:text-sm text-red-100/90 font-semibold uppercase tracking-widest">
                        Nuevas compras deshabilitadas
                      </p>
                    </div>
                  </div>

                  {/* Admin-configured reason */}
                  {siteStatus.reason ? (
                    <p
                      data-testid="site-closed-reason"
                      className="max-w-2xl text-sm sm:text-base text-zinc-100 leading-relaxed whitespace-pre-wrap px-4"
                    >
                      {siteStatus.reason}
                    </p>
                  ) : (
                    <p className="max-w-2xl text-sm sm:text-base text-zinc-300 leading-relaxed px-4">
                      Serás notificado cuando estemos activos de nuevo.
                    </p>
                  )}

                  {/* Reopen waitlist CTA — public "Avisarme al abrir" */}
                  <div
                    data-testid="reopen-notify-card"
                    className="w-full max-w-lg mx-auto flex flex-col items-center gap-2 rounded-2xl border border-cyan-500/40 bg-cyan-500/[0.06] backdrop-blur-sm px-4 py-4"
                  >
                    <p className="text-[10px] sm:text-xs font-mono uppercase tracking-[0.35em] text-cyan-300/80">
                      ¿No quieres perdértelo?
                    </p>
                    <p className="text-sm text-zinc-100 font-semibold">
                      Recibe una notificación en cuanto la tienda vuelva a abrir.
                    </p>
                    <ReopenNotifyButton showToast={showToast} />
                  </div>

                  {/* Follow-us CTA using active social networks */}
                  {socialNetworks.length > 0 && (
                    <div className="w-full max-w-lg space-y-2">
                      <p className="text-[10px] sm:text-xs font-mono uppercase tracking-[0.35em] text-cyan-300/80">
                        Síguenos para mantenerte informado
                      </p>
                      <div className="flex flex-wrap justify-center gap-2">
                        {socialNetworks?.map((net) => (
                          <a
                            key={net.id}
                            href={net.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`closed-social-link-${net.id}`}
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/5 hover:bg-white/10 border border-cyan-400/40 text-cyan-100 text-xs font-bold transition-all backdrop-blur-sm"
                          >
                            {net.icon
                              ? <span className="text-base leading-none">{net.icon}</span>
                              : <ArrowUpRight className="w-3.5 h-3.5" />}
                            <span>{net.name}</span>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  <p className="text-[10px] font-mono text-zinc-500 pt-2 border-t border-zinc-800/60 w-full max-w-md">
                    Puedes seguir viendo tus pedidos previos, chat de soporte y saldo a favor.
                  </p>
                </div>
              </div>
            )}
            <div
              data-testid="client-tabs-bar"
              className="relative flex flex-wrap items-center gap-1.5 sm:gap-2 border-b border-zinc-800/80 pb-3"
            >
              <TabBtn data-testid="client-tab-store" active={clientTab === "store"} onClick={() => { setClientTab("store"); setSelectedGame(null); }} testId="client-tab-store" icon={ShoppingCart} iconColor="text-purple-400">TIENDA</TabBtn>
              <TabBtn data-testid="client-tab-cart" active={clientTab === "cart"} onClick={() => setClientTab("cart")} testId="client-tab-cart" icon={ShoppingCart} iconColor="text-green-400">
                CARRITO {cart.length > 0 && <span className="ml-1 px-1.5 py-0.5 rounded-full bg-green-600 text-white text-[10px] font-bold">{cart.length}</span>}
              </TabBtn>
              <TabBtn data-testid="client-tab-orders" active={clientTab === "orders"} onClick={() => setClientTab("orders")} testId="client-tab-orders" icon={FileText} iconColor="text-cyan-400">HISTORIAL</TabBtn>
              <TabBtn data-testid="client-tab-tournaments" active={clientTab === "tournaments"} onClick={() => setClientTab("tournaments")} testId="client-tab-tournaments" icon={Trophy} iconColor={activeTournamentCount > 0 ? "text-orange-300" : "text-amber-400"}><span data-testid="tournaments-nav-label" className={`inline-flex items-center ${activeTournamentCount > 0 ? "font-black text-amber-200 [text-shadow:0_0_10px_rgba(251,146,60,0.95)]" : ""}`}>{activeTournamentCount > 0 && <span data-testid="tournaments-nav-fire" aria-hidden="true" className="mr-0.5 animate-[pulse_0.7s_ease-in-out_infinite] text-sm drop-shadow-[0_0_7px_rgba(251,68,0,1)]">🔥</span>}TORNEOS</span></TabBtn>
              <TabBtn data-testid="client-tab-referrals" active={clientTab === "referrals"} onClick={() => setClientTab("referrals")} testId="client-tab-referrals" icon={Gift} iconColor="text-pink-400">REFERIDOS</TabBtn>

              {/* Interactive tour launcher — persistent, always visible */}
              <TourLauncherButton />

              {/* Prominent Wallet chip (Saldo a favor) */}
              <WalletChip data-testid="app-wallet-chip-3"
                active={clientTab === "wallet"}
                amount={user.credit_balance_bs || 0}
                rewardPulse={referralRewardPulse}
                onClick={() => setClientTab("wallet")}
              />
            </div>

            {/* Reseller (mayorista) discount banner */}
            {isReseller && (
              <div
                data-testid="reseller-badge"
                className="mx-auto max-w-3xl mt-2 flex items-center gap-3 px-4 py-2.5 rounded-2xl border border-amber-500/50 bg-gradient-to-r from-amber-950/60 via-orange-950/40 to-amber-950/60 shadow-[0_0_20px_-6px_rgba(245,158,11,0.55)]"
              >
                <div className="w-8 h-8 rounded-full bg-amber-500/20 border border-amber-400/60 flex items-center justify-center flex-shrink-0">
                  <DollarSign className="w-4 h-4 text-amber-300" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-mono uppercase tracking-widest text-amber-300 leading-none">Cuenta Revendedor</p>
                  <p className="text-sm font-black text-amber-100 leading-tight">Precio Mayorista / Revendedor Activo</p>
                </div>
                <span className="hidden sm:inline text-[10px] font-mono text-amber-200/70">Descuento aplicado</span>
              </div>
            )}

            {clientTab === "store" && !selectedGame && (
              <ReferralBanner summary={referralData} onOpen={() => setClientTab("referrals")} />
            )}

            {/* STORE - GAMES GRID */}
            {clientTab === "store" && !selectedGame && siteStatus.open && (
              <GameCatalogPage
                games={games}
                gamesLoaded={gamesLoaded}
                setGames={setGames}
                setSelectedGame={setSelectedGame}
                setSelectedPackage={setSelectedPackage}
                setPlayerId={setPlayerId}
                setPlayerPassword={setPlayerPassword}
                setPlayerServer={setPlayerServer}
                setAccessMethod={setAccessMethod}
                setVerifiedPlayerName={setVerifiedPlayerName}
                setIdVerificationStatus={setIdVerificationStatus}
                showToast={showToast}
              />
            )}

            {/* STORE - PACKAGES */}
            {clientTab === "store" && selectedGame && siteStatus.open && selectedGame.is_active === false && (
              <div className="space-y-6" data-testid="game-inactive-block">
                <div className="flex items-center justify-between border-b border-zinc-800 pb-4">
                  <div className="flex items-center gap-3">
                    <button onClick={() => setSelectedGame(null)} data-testid="back-to-store-btn-inactive"
                      className="px-4 py-2 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-white text-xs font-bold">← VOLVER</button>
                    <div>
                      <h3 className="text-xl font-bold text-white">{selectedGame.name}</h3>
                      <p className="text-xs text-red-400 font-mono">🔧 Fuera de servicio</p>
                    </div>
                  </div>
                </div>
                <div className="max-w-lg mx-auto text-center rounded-3xl bg-[#0f1015] border border-red-500/30 p-10 space-y-4">
                  <div className="w-16 h-16 rounded-full bg-red-950/50 border border-red-500/50 flex items-center justify-center mx-auto">
                    <AlertTriangle className="w-8 h-8 text-red-400" />
                  </div>
                  <h4 className="text-lg font-bold text-white">Juego temporalmente pausado</h4>
                  <p className="text-sm text-zinc-400">
                    Este juego se encuentra temporalmente fuera de servicio. Reintenta más tarde.
                  </p>
                  <button data-testid="app-button-4"
                    onClick={() => setSelectedGame(null)}
                    className="mt-2 px-4 py-2 rounded-xl bg-gradient-to-r from-purple-600 to-cyan-600 text-white text-xs font-bold"
                  >
                    Ver otros juegos
                  </button>
                </div>
              </div>
            )}

            {/* STORE - PACKAGES */}
            {clientTab === "store" && selectedGame && siteStatus.open && selectedGame.is_active !== false && (
              <StoreDetailPage />
            )}

            {/* CART VIEW */}
            {clientTab === "cart" && (
              <div className="space-y-6" data-testid="client-cart-view">
                <div className="relative overflow-hidden bg-[var(--ag7-glass)] backdrop-blur-xl border border-ag7-purple/25 rounded-2xl p-6">
                  <div className="pointer-events-none absolute -top-14 -right-10 w-44 h-44 rounded-full bg-ag7-magenta/15 blur-3xl" aria-hidden="true" />
                  <div className="relative flex items-center gap-4">
                    <div className="w-11 h-11 rounded-2xl bg-ag7-magenta/15 border border-ag7-magenta/50 shadow-[0_0_18px_rgba(236,72,153,0.25)] flex items-center justify-center flex-shrink-0" aria-hidden="true">
                      <ShoppingCart className="w-5 h-5 text-ag7-magenta" />
                    </div>
                    <div>
                      <h3 className="text-xl font-heading font-black tracking-tight text-white uppercase">Tu Carrito de Compras</h3>
                      <p className="text-xs text-[var(--ag7-text-muted)]">Verifica los productos antes de pagar. Pagas todo con una sola referencia.</p>
                    </div>
                  </div>
                </div>

                {cart.length === 0 ? (
                  <div className="text-center py-12 space-y-3">
                    <ShoppingCart className="w-12 h-12 text-zinc-700 mx-auto" />
                    <p className="text-zinc-500 text-sm">Tu carrito está vacío</p>
                    <button onClick={() => setClientTab("store")} data-testid="empty-cart-go-store-btn"
                      className="bg-gradient-to-r from-ag7-purple to-ag7-magenta hover:opacity-90 text-white font-bold px-6 py-2.5 rounded-xl text-xs shadow-md shadow-ag7-purple/30 transition-opacity">
                      Ir a la Tienda
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="rounded-2xl bg-ag7-surface border border-[var(--ag7-border-soft)] divide-y divide-zinc-800/70">
                      {cart.map((it, idx) => (
                        <div key={it.tempId} className="p-4 flex items-center gap-4" data-testid={`cart-item-${idx}`}>
                          <div className="w-14 h-14 rounded-xl overflow-hidden border border-[var(--ag7-border)] flex-shrink-0">
                            <img src={it.gameImage} alt="" className="w-full h-full object-cover" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-heading font-bold text-white text-sm">{it.gameName}</p>
                            <p className="text-ag7-purple text-xs">{it.packageName}</p>
                            <p className="text-[10px] text-zinc-500 font-mono truncate">
                              {it.credentialType === "username_password" ? `👤 ${it.playerId} · 🔒 contraseña incluida` : `ID: ${it.playerId}`}
                            </p>
                            {Number(it.referralDiscountUsd) > 0 && (
                              <p data-testid={`cart-referral-discount-${idx}`} className="mt-1 text-[10px] font-mono font-bold text-green-300">✓ Beneficio de referido aplicado</p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-extrabold text-ag7-cyan font-mono tabular-nums">{formatItemMoney(it).primary}</p>
                            <p className="text-[10px] text-zinc-500 font-mono tabular-nums">{formatItemMoney(it).secondary}</p>
                            <button onClick={() => handleRemoveFromCart(it.tempId)} data-testid={`remove-cart-item-${idx}`}
                              className="mt-1 text-red-400 hover:text-red-300 text-xs flex items-center gap-1 ml-auto">
                              <Trash2 className="w-3 h-3" /> Quitar
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="rounded-3xl bg-[var(--ag7-glass)] backdrop-blur-xl border-2 border-ag7-purple/40 shadow-[var(--ag7-glow-purple)] p-6 space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-mono text-ag7-purple tracking-widest uppercase">Total a Pagar</p>
                          <p className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[var(--ag7-neon-cyan)] to-[var(--ag7-neon-magenta)] font-mono tabular-nums mt-1" data-testid="cart-total">
                            {currency === "USD" ? formatUsd(cartTotalUsd) : formatBs(cartTotalBs)}
                          </p>
                          <p className="text-[11px] text-zinc-500 font-mono mt-1">
                            {currency === "USD" ? formatBs(cartTotalBs) : formatUsd(cartTotalUsd)} · {cart.length} producto{cart.length !== 1 ? "s" : ""}
                          </p>
                        </div>
                        <button onClick={() => setClientTab("store")} data-testid="cart-add-more-btn"
                          className="bg-ag7-surface-2 hover:bg-zinc-700 border border-[var(--ag7-border-soft)] text-white font-bold px-4 py-2.5 rounded-xl text-xs flex items-center gap-2 transition-colors">
                          <Plus className="w-3.5 h-3.5" /> Añadir más
                        </button>
                      </div>
                      <button onClick={() => { setValidationErrors([]); setPaidAmountBs(String(cartTotalBs)); setShowPaymentModal(true); }}
                        data-testid="checkout-btn"
                        className="w-full bg-gradient-to-r from-ag7-purple via-ag7-magenta to-ag7-purple hover:opacity-95 text-white font-black py-4 rounded-2xl text-sm shadow-2xl shadow-ag7-purple/30 tracking-wider flex items-center justify-center gap-2 transition-opacity">
                        <CreditCard className="w-4 h-4" />
                        <span>PROCEDER AL PAGO</span>
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* ORDERS HISTORY */}
            {clientTab === "orders" && (
              <OrdersPage
                orders={orders}
                onCompletePartial={openCompletePartial}
                onOpenChat={setChatOrder}
                onPayAdjustment={setAdjustPayOrder}
                onAdjustmentResolved={loadOrders}
                showToast={showToast}
              />
            )}

            {clientTab === "tournaments" && (
              <TournamentsPage paymentMethods={paymentMethods} showToast={showToast} />
            )}

            {clientTab === "referrals" && (
              <ReferralPage
                summary={referralData}
                loading={referralLoading}
                rewardPulse={referralRewardPulse}
                onCopy={copyReferralLink}
                onShare={shareReferralLink}
              />
            )}

            {/* WALLET */}
            {clientTab === "wallet" && (
              <div className="space-y-6" data-testid="client-wallet-view">
                <div className="relative overflow-hidden rounded-3xl bg-[var(--ag7-glass)] backdrop-blur-xl border border-ag7-purple/40 shadow-[var(--ag7-glow-purple)] p-8">
                  <div className="pointer-events-none absolute -top-16 -right-12 w-52 h-52 rounded-full bg-ag7-purple/20 blur-3xl" aria-hidden="true" />
                  <div className="relative flex items-center gap-4 mb-2">
                    <div className="w-11 h-11 rounded-2xl bg-green-500/10 border border-green-500/50 shadow-[0_0_18px_rgba(34,197,94,0.2)] flex items-center justify-center flex-shrink-0" aria-hidden="true">
                      <DollarSign className="w-5 h-5 text-green-400" />
                    </div>
                    <h3 className="text-xl font-heading font-black tracking-tight text-white uppercase">Saldo a Favor en Cuenta</h3>
                  </div>
                  <p className="relative text-xs text-[var(--ag7-text-muted)] mb-6">Excedentes por pagos superiores acreditados para tus próximas compras.</p>
                  <div className="relative p-6 rounded-2xl bg-ag7-bg/80 border border-[var(--ag7-border-soft)] flex items-center justify-between">
                    <div>
                      <span className="text-xs font-mono text-[var(--ag7-text-muted)] uppercase tracking-widest">Balance Disponible</span>
                      <h4 className="text-3xl font-extrabold text-green-400 font-mono tabular-nums mt-1 [text-shadow:0_0_18px_rgba(34,197,94,0.35)]" data-testid="wallet-balance">
                        Bs {Math.round((user.credit_balance_bs || 0))}
                      </h4>
                    </div>
                    <button data-testid="app-button-5" onClick={() => setClientTab("store")} className="bg-gradient-to-r from-ag7-purple to-ag7-magenta hover:opacity-90 text-white font-bold px-6 py-3 rounded-xl text-xs shadow-lg shadow-ag7-purple/30 transition-opacity">
                      Usar en Tienda
                    </button>
                  </div>
                </div>

                {/* Retiros de revendedor — módulo independiente, solo visible
                    para el rol "reseller". Ver /components/client/ResellerWithdrawals.jsx */}
                {user.role === "reseller" && (
                  <div className="relative isolate transform-gpu overflow-hidden rounded-3xl bg-[var(--ag7-glass)] backdrop-blur-xl border border-emerald-500/30 p-6 sm:p-8">
                    <ResellerWithdrawals user={user} showToast={showToast} refreshUser={refreshUser} />
                  </div>
                )}
              </div>
            )}

            {/* Social Networks — visible on every client tab */}
            <SocialNetworksBar networks={socialNetworks} />
          </div>
        )}

        {/* ============ ADMIN PANEL (extracted to /pages/AdminPage.jsx) ============ */}
        {activeRole === "admin" && canSwitchAdmin && <AdminPage />}
      </main>

      {/* ============ MODALS (extracted to /pages/AppModals.jsx) ============ */}
      <CurrencyUsdConfirmDialog
        open={confirmUsdSwitch}
        onCancel={() => setConfirmUsdSwitch(false)}
        onConfirm={() => { setCurrency("USD"); setConfirmUsdSwitch(false); }}
      />
      <AppModals />
    </div>
    </AppProvider>
  );
}

